"use client";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { usePathname, useRouter } from "next/navigation";
import { JSX } from "react";

/**
 * SiteHeader Component
 *
 * Displays the application header with sidebar trigger, separator, and interactive breadcrumb navigation.
 * The breadcrumb shows the current page hierarchy and allows navigation to parent pages by clicking
 * on breadcrumb segments.
 *
 * Features:
 * - Interactive breadcrumb navigation
 * - Current page is always visually highlighted
 * - Parent segments are clickable for navigation
 * - Responsive design with proper spacing
 *
 * @returns {JSX.Element} The site header component
 */
export function SiteHeader(): JSX.Element {
  const pathname = usePathname();
  const router = useRouter();

  const currentBreadcrumb = pathname?.split("/")?.filter(Boolean);

  // console.log("Current breadcrumb:", currentBreadcrumb);

  /**
   * Handles breadcrumb segment clicks to navigate to parent pages.
   *
   * @param {number} index - The index of the clicked breadcrumb segment (0-based)
   * @example
   * // If current path is "/event/venue/page" and index 0 is clicked:
   * handleBreadcrumbClick(0) // navigates to "/event"
   * handleBreadcrumbClick(1) // navigates to "/event/venue"
   */
  const handleBreadcrumbClick = (index: number) => {
    // Build the path up to the clicked segment
    const pathSegments = currentBreadcrumb?.slice(0, index + 1) || [];
    const targetPath = "/" + pathSegments.join("/");
    router.push(targetPath);
  };

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-2 data-[orientation=vertical]:h-4"
        />
        <div className="flex items-center gap-1 text-sm font-medium">
          {currentBreadcrumb && currentBreadcrumb.length > 0 ? (
            currentBreadcrumb.map((segment, index) => {
              const isLastSegment = index === currentBreadcrumb.length - 1;
              const displayText =
                segment.charAt(0).toUpperCase() + segment.slice(1);

              return (
                <span key={index} className="flex items-center gap-1">
                  {index > 0 && (
                    <span className="text-muted-foreground">/</span>
                  )}
                  {isLastSegment ? (
                    <span>{displayText}</span>
                  ) : (
                    <button
                      onClick={() => handleBreadcrumbClick(index)}
                      className="hover:text-primary transition-colors capitalize cursor-pointer hover:underline decoration-2 underline-offset-2"
                      title={`Navigate to ${displayText}`}
                    >
                      {displayText}
                    </button>
                  )}
                </span>
              );
            })
          ) : (
            <span className="underline decoration-2 underline-offset-2">
              Dashboard
            </span>
          )}
        </div>
        {/* <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
        </div> */}
      </div>
    </header>
  );
}
